import { convertQueryCommandToQueryString, IQueryCommandOptions, Null, Or, GreaterThan } from '@llws/dynamic-router-utils';
import { IPremiumReportingClientCities } from '@llws/lift-entity-interfaces';
import dayjs from 'dayjs';
import { ActionContext } from 'vuex';
import { StoreBaseImplementationV2 } from '@/shared/classes/store-base-implementation-v2';
import { IAction, IStateStrict } from '@/shared/interfaces/store-base';
import { adminRequest } from '@/shared/request-configs/admin-request';

const defaultFilters = {
  alias: 'prcc',
  relations: [
    {
      path: 'city',
      alias: 'gc',
    },
    {
      path: 'gc.premiumReportingCitiesBaseSubscriptions',
      alias: 'prcbs',
    },
    {
      path: 'gc.province',
      alias: 'p',
    },
    {
      path: 'subscription',
      alias: 's',
    },
    {
      path: 's.baseSubscription',
      alias: 'bs',
    },
    {
      path: 'bs.service',
      alias: 'ser',
    },
  ],
};

const adminGatewayResourcePath: string = '/client-api/premium_reporting_client_cities';
const listViewName: string = '';
const showLogging: boolean = false;
const defaultGetListFilters: IQueryCommandOptions = defaultFilters;
const defaultGetSingleFilters: IQueryCommandOptions = {};
const base = new StoreBaseImplementationV2<Partial<IPremiumReportingClientCities>>({
  adminGatewayResourcePath,
  defaultGetSingleFilters,
  defaultGetListFilters,
  listViewName,
  showLogging,
});

export default {
  namespaced: base.namespaced,

  state: {
    ...base.state,
    listLite: [],
  },

  getters: {
    ...base.getters,

    /**
     * Returns state.list as a unique list of cities, starting by removing any cities with inactive subscriptions.
     * @param state
     * @returns
     */
    defaultCitySelectList: (state: IStateStrict<IPremiumReportingClientCities>) => {
      if (!state.list?.data) {
        return [];
      }

      const now = dayjs();
      const nullExpiryStandIn = dayjs().add(1, 'day');

      return state.list?.data.reduce<IPremiumReportingClientCities[]>((acc, cur) => {
        const expiryDate = (
          cur.subscription?.expiryDate
            ? dayjs(cur.subscription.expiryDate)
            : nullExpiryStandIn
        );

        if (expiryDate.isAfter(now, 'day') && !acc.some((city) => city.cityId === cur.cityId)) {
          acc.push(cur);
        }

        return acc;
      }, []);
    },

    listNormalized: (state: IStateStrict<IPremiumReportingClientCities>) => {
      if (!state.list?.data) {
        return [];
      }

      const now = dayjs();
      const nullExpiryStandIn = dayjs().add(1, 'day');

      return state.list?.data
        .filter((c) => {
          const expiryDate = c.subscription?.expiryDate ? dayjs(c.subscription.expiryDate) : nullExpiryStandIn;
          return expiryDate.isAfter(now, 'day');
        })
        .map((prc) => ({
          ...prc,
          city: {
            ...prc.city,
            cityName: `${prc.city?.cityName.normalize('NFD').replace(/[\u0300-\u036f]/g, '')}`,
          },
        }))
        .sort((a, b) => (
          (a.cityDefaultPriority ?? 9998) < (b.cityDefaultPriority ?? 9999)
            ? -1
            : 1
        ));
    },

    listLite: (
      state: IStateStrict<IPremiumReportingClientCities> & { listLite?: IPremiumReportingClientCities[] },
    ) => state.listLite ?? [],
  },

  mutations: {
    ...base.mutations,

    setListLite: (
      state: IStateStrict<IPremiumReportingClientCities> & { listLite?: IPremiumReportingClientCities[] },
      payload: IPremiumReportingClientCities[],
    ) => {
      state.listLite = payload;
    },
  },

  actions: {
    ...base.actions,

    getLiteList: async (ctx: IAction<IStateStrict<typeof base.state>>) => {
      const now = dayjs().format('YYYY-MM-DD');

      const query: IQueryCommandOptions = {
        alias: 'prcc',
        select: [
          'prcc.id',
          'prcc.clientId',
          'prcc.subscriptionId',
          'prcc.cityId',
        ],
        relations: [
          { path: 'prcc.subscription', alias: 'sub' },
        ],
        where: [
          {
            condition: 'and',
            whereCommands: [
              Or(Null('sub.expiryDate')),
              Or(GreaterThan('sub.expiryDate', now)),
            ],
          },
        ],
        orderBy: [
          { field: 'sub.startDate', direction: 'asc' },
        ],
        limit: 'false',
      };

      const res = await adminRequest.get<IPremiumReportingClientCities>(`${adminGatewayResourcePath}${convertQueryCommandToQueryString(query)}`);
      ctx.commit('setListLite', res.data ?? []);
    },
  },
};
